import type {
    NounTable,
    Purusha,
    Vacana,
    VerbTable,
    Vibhakti
} from '@/sanskrit/lib/sanskrit/types'

import { cn } from '@/lib/utils'
import { PURUSHAS, VACANAS, VIBHAKTIS } from '@/sanskrit/lib/sanskrit/types'
import { PURUSHA_LABEL, VACANA_LABEL, VIBHAKTI_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { useT } from '@/sanskrit/components/providers/app-state'

interface VerbTableViewProps {
    forms: VerbTable
    highlight?: { p: Purusha; v: Vacana } | null
    onCellClick?: (p: Purusha, v: Vacana) => void
    hidden?: Set<string>
    revealed?: Set<string>
    compact?: boolean
    caption?: string
    className?: string
}

export const cellKey = (a: string | number, b: string) => `${a}:${b}`

/** 3 x 3 conjugation grid: rows are पुरुष (प्रथम, मध्यम, उत्तम), columns are वचन. */
export function VerbTableView({ forms, highlight, onCellClick, hidden, revealed, compact, caption, className }: VerbTableViewProps) {
    const t = useT()

    return (
        <div className={cn('overflow-x-auto rounded-2xl border border-border bg-card shadow-sm', className)}>
            <table className='w-full min-w-[22rem] border-collapse text-left'>
                {caption && <caption className='sr-only'>{caption}</caption>}
                <thead>
                    <tr className='bg-muted/60'>
                        <th scope='col' className='px-3 py-2.5 text-xs font-semibold text-muted-foreground'>
                            <span className='deva'>पुरुषः</span> / <span className='deva'>वचनम्</span>
                        </th>
                        {VACANAS.map(v => (
                            <th key={v} scope='col' className='px-3 py-2.5 text-xs font-semibold text-muted-foreground'>
                                <span className='deva block text-sm text-foreground'>{VACANA_LABEL[v].sa}</span>
                                <span>{t({ hi: VACANA_LABEL[v].hi, en: VACANA_LABEL[v].en })}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {PURUSHAS.map(p => (
                        <tr key={p} className='border-t border-border'>
                            <th scope='row' className='px-3 py-2.5 align-top text-xs font-semibold text-muted-foreground'>
                                <span className='deva block text-sm text-foreground'>{PURUSHA_LABEL[p].short}</span>
                                <span>{t({ hi: PURUSHA_LABEL[p].hi, en: PURUSHA_LABEL[p].en })}</span>
                            </th>
                            {VACANAS.map(v => {
                                const cell = forms[p][v]
                                const key = cellKey(p, v)
                                const isHidden = hidden?.has(key) && !revealed?.has(key)
                                const active = highlight?.p === p && highlight?.v === v
                                const Inner = (
                                    <>
                                        <span lang='sa' className={cn('deva block font-semibold', compact ? 'text-lg' : 'text-xl')}>
                                            {isHidden ? ' ? ' : cell[0]}
                                        </span>
                                        {!isHidden && cell.length > 1 && (
                                            <span lang='sa' className='deva block text-xs text-muted-foreground'>
                                                {cell.slice(1).join(' / ')}
                                            </span>
                                        )}
                                    </>
                                )

                                return (
                                    <td key={v} className='p-1.5'>
                                        {onCellClick ? (
                                            <button
                                                type='button'
                                                onClick={() => onCellClick(p, v)}
                                                aria-pressed={active}
                                                className={cn(
                                                    'w-full rounded-xl px-2.5 py-2 text-left transition-all',
                                                    active ? 'bg-saffron text-ink shadow-md ring-2 ring-saffron-strong/40' : 'hover:bg-saffron-soft',
                                                    isHidden && 'border border-dashed border-saffron-strong/50 bg-saffron-soft/50 text-saffron-strong'
                                                )}
                                            >
                                                {Inner}
                                            </button>
                                        ) : (
                                            <div className={cn('rounded-xl px-2.5 py-2', active && 'bg-saffron text-ink')}>
                                                {Inner}
                                            </div>
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

interface NounTableViewProps {
    table: NounTable
    highlight?: { vib: Vibhakti; v: Vacana } | null
    onCellClick?: (vib: Vibhakti, v: Vacana) => void
    rows?: Vibhakti[]
    hidden?: Set<string>
    revealed?: Set<string>
    compact?: boolean
    className?: string
}

/** 8 x 3 declension grid. The vocative row shows हे by convention. */
export function NounTableView({ table, highlight, onCellClick, rows = VIBHAKTIS, hidden, revealed, compact, className }: NounTableViewProps) {
    const t = useT()

    return (
        <div className={cn('overflow-x-auto rounded-2xl border border-border bg-card shadow-sm', className)}>
            <table className='w-full min-w-[22rem] border-collapse text-left'>
                <thead>
                    <tr className='bg-muted/60'>
                        <th scope='col' className='px-3 py-2.5 text-xs font-semibold text-muted-foreground'>
                            <span className='deva'>विभक्तिः</span>
                        </th>
                        {VACANAS.map(v => (
                            <th key={v} scope='col' className='px-3 py-2.5 text-xs font-semibold text-muted-foreground'>
                                <span className='deva block text-sm text-foreground'>{VACANA_LABEL[v].sa}</span>
                                <span>{t({ hi: VACANA_LABEL[v].hi, en: VACANA_LABEL[v].en })}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(vib => (
                        <tr key={vib} className='border-t border-border'>
                            <th scope='row' className='px-3 py-2 align-top text-xs font-semibold text-muted-foreground'>
                                <span className='deva block text-sm text-foreground'>{VIBHAKTI_LABEL[vib].sa}</span>
                                <span>{t({ hi: VIBHAKTI_LABEL[vib].karaka, en: VIBHAKTI_LABEL[vib].en })}</span>
                            </th>
                            {VACANAS.map(v => {
                                const cell = table[vib][v]
                                const key = cellKey(vib, v)
                                const isHidden = hidden?.has(key) && !revealed?.has(key)
                                const active = highlight?.vib === vib && highlight?.v === v
                                const empty = cell.length === 0
                                const Inner = (
                                    <>
                                        <span lang='sa' className={cn('deva block font-semibold', compact ? 'text-base' : 'text-lg')}>
                                            {empty ? '—' : isHidden ? ' ? ' : `${vib === 8 ? 'हे ' : ''}${cell[0]}`}
                                        </span>
                                        {!isHidden && cell.length > 1 && (
                                            <span lang='sa' className='deva block text-xs text-muted-foreground'>
                                                {cell.slice(1).join(' / ')}
                                            </span>
                                        )}
                                    </>
                                )

                                return (
                                    <td key={v} className='p-1'>
                                        {onCellClick && !empty ? (
                                            <button
                                                type='button'
                                                onClick={() => onCellClick(vib, v)}
                                                aria-pressed={active}
                                                className={cn(
                                                    'w-full rounded-xl px-2.5 py-1.5 text-left transition-all',
                                                    active ? 'bg-saffron text-ink shadow-md ring-2 ring-saffron-strong/40' : 'hover:bg-saffron-soft',
                                                    isHidden && 'border border-dashed border-saffron-strong/50 bg-saffron-soft/50 text-saffron-strong'
                                                )}
                                            >
                                                {Inner}
                                            </button>
                                        ) : (
                                            <div className={cn('rounded-xl px-2.5 py-1.5', active && 'bg-saffron text-ink', empty && 'text-muted-foreground')}>
                                                {Inner}
                                            </div>
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
